import { Loader2, CheckCircle2, AlertTriangle, Download } from 'lucide-react';
import { useSyncStore } from '@/store/syncStore';
import { closeCatalogSyncPopup } from '@/offline/chunkedBootstrap';

export default function CatalogSyncModal() {
  const progress = useSyncStore((s) => s.catalogSyncProgress);
  if (!progress?.open) return null;

  const pct =
    progress.overallTotal > 0
      ? Math.min(100, Math.round((progress.overallDone / progress.overallTotal) * 100))
      : progress.phase === 'done'
        ? 100
        : 5;

  const isError = progress.phase === 'error';
  const isDone = progress.phase === 'done';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 pt-6 pb-4 text-center">
          <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center">
            {isError ? (
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            ) : isDone ? (
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            ) : (
              <Download className="w-7 h-7 text-primary-600" />
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            {isError ? 'Catalog sync failed' : isDone ? 'Local catalog ready' : 'Syncing to local store'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {isError
              ? progress.error || progress.label
              : 'Downloading products, customers and suppliers for Offline Always / Local First.'}
          </p>
        </div>

        <div className="px-6 pb-2">
          <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                isError ? 'bg-amber-500' : isDone ? 'bg-emerald-500' : 'bg-primary-600'
              }`}
              style={{ width: `${isError ? Math.max(pct, 8) : pct}%` }}
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              {!isError && !isDone && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-600" />}
              {progress.label || 'Working…'}
            </span>
            <span className="font-medium text-gray-700">
              {progress.overallDone} / {progress.overallTotal}
            </span>
          </div>
          {progress.entityTotal > 0 && progress.phase !== 'meta' && progress.phase !== 'finalizing' && !isDone && !isError && (
            <p className="mt-1 text-[11px] text-gray-400">
              Current batch: {progress.entityDone} / {progress.entityTotal}
            </p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          {isError ? (
            <button
              type="button"
              onClick={() => closeCatalogSyncPopup()}
              className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
            >
              Close
            </button>
          ) : isDone ? (
            <button
              type="button"
              onClick={() => closeCatalogSyncPopup()}
              className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700"
            >
              Continue
            </button>
          ) : (
            <p className="text-xs text-gray-400 w-full text-center py-1">Please keep this window open…</p>
          )}
        </div>
      </div>
    </div>
  );
}
